import { useEffect, useState } from "react";
import { api, fmtInt } from "@/lib/api";
import MovementModal from "@/components/inventory/MovementModal";
import { Package, ArrowDown, ArrowUp, Plus, Minus } from "@phosphor-icons/react";
import HintButton from "@/components/HintButton";

/**
 * Página dedicada de Entrada/Saída de Estoque — acessível pelo menu lateral.
 * Colaborador escolhe se quer dar entrada ou saída, e o modal padrão de
 * MovementModal é aberto no modo correspondente.
 */
export default function StockMovement() {
  const [invTypes, setInvTypes] = useState([]);
  const [totalStock, setTotalStock] = useState(0);
  const [openMode, setOpenMode] = useState(null); // "in" | "out" | null
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const [{ data: types }, { data: summary }] = await Promise.all([
        api.get("/inventory/types"),
        api.get("/inventory/summary").catch(() => ({ data: null })),
      ]);
      setInvTypes(types || []);
      if (summary) setTotalStock(summary.total_units || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8">
        <div className="overline text-zinc-500 mb-2">OPERAÇÃO DA FÁBRICA</div>
        <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Entrada / Saída de Estoque</h1>
          <HintButton text="Escolha o tipo de movimentação. O colaborador precisa autenticar cada operação com seu código." />
      </div>

      {/* Resumo rápido */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px] bg-black border border-black mb-8" data-testid="stock-summary">
        <div className="bg-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <Package size={24} weight="bold" />
            <div className="overline text-zinc-500">TOTAL EM ESTOQUE</div>
          </div>
          <div className="font-mono text-4xl font-black">{loading ? "…" : fmtInt(totalStock)}</div>
          <div className="text-xs text-zinc-500 mt-1">unidades cadastradas</div>
        </div>
        <div className="bg-white p-6">
          <div className="overline text-zinc-500 mb-2">TIPOS DE COPOS</div>
          <div className="font-mono text-4xl font-black">{invTypes.length}</div>
          <div className="text-xs text-zinc-500 mt-1">variações disponíveis</div>
        </div>
        <div className="bg-black text-white p-6">
          <div className="overline text-white/60 mb-2">DICA</div>
          <div className="text-sm mt-1">Use este atalho no início de cada turno ou quando chegar mercadoria nova. Cada movimento fica registrado com o código de quem fez.</div>
        </div>
      </div>

      {/* CTAs principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <button
          onClick={() => setOpenMode("in")}
          className="border-2 border-black bg-white hover:bg-[#E8F5E9] p-8 text-left transition-colors group"
          data-testid="btn-stock-in"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-4 bg-[#00A300] text-white group-hover:scale-110 transition-transform">
              <ArrowDown size={32} weight="bold" />
            </div>
            <Plus size={40} weight="bold" className="text-[#00A300]" />
          </div>
          <div className="overline text-zinc-500">ENTRADA</div>
          <h2 className="font-head text-3xl font-black mt-1 mb-2">Dar Entrada</h2>
          <p className="text-sm text-zinc-600">Chegou mercadoria nova? Registre a entrada informando tipo, cor e quantidade.</p>
        </button>

        <button
          onClick={() => setOpenMode("out")}
          className="border-2 border-black bg-white hover:bg-red-50 p-8 text-left transition-colors group"
          data-testid="btn-stock-out"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-4 bg-[#E60000] text-white group-hover:scale-110 transition-transform">
              <ArrowUp size={32} weight="bold" />
            </div>
            <Minus size={40} weight="bold" className="text-[#E60000]" />
          </div>
          <div className="overline text-zinc-500">SAÍDA</div>
          <h2 className="font-head text-3xl font-black mt-1 mb-2">Dar Saída</h2>
          <p className="text-sm text-zinc-600">Registrar consumo manual (fora do fluxo automático do Kanban). Ex.: quebra, ajuste.</p>
        </button>
      </div>

      {openMode && (
        <MovementModal
          mode={openMode}
          types={invTypes}
          onClose={() => setOpenMode(null)}
          onSaved={() => { setOpenMode(null); load(); }}
        />
      )}
    </div>
  );
}
